/**
 * Shared tactile game pieces for Camel Up: pip dice, gold coins, leg-bet
 * "betting slips", race-bet cards, and the wordmark. Pure presentational
 * components built from divs + small SVGs so they're cheap to render in
 * stacks and lists.
 */

import type { CSSProperties, ReactNode } from 'react'
import type { CamelColor, DieId, RacingColor } from '@mdev/common'
import { CAMEL_HEX, CU, CU_DISPLAY, CU_HEADING } from '../theme'
import { CamelGlyph } from './CamelGlyph'

// ---------------------------------------------------------------------------
// Pip die
// ---------------------------------------------------------------------------

const DIE_FILL: Record<DieId, { light: string; body: string; dark: string; ink: string }> = {
  red: { ...CAMEL_HEX.red },
  yellow: { ...CAMEL_HEX.yellow },
  green: { ...CAMEL_HEX.green },
  blue: { ...CAMEL_HEX.blue },
  purple: { ...CAMEL_HEX.purple },
  grey: { light: '#6b6b73', body: '#4a4a52', dark: '#2c2c33', ink: '#fff' },
}

/** 3x3 pip layout, percentages, for die faces 1..3. */
const PIPS: Record<1 | 2 | 3, Array<[number, number]>> = {
  1: [[50, 50]],
  2: [
    [29, 29],
    [71, 71],
  ],
  3: [
    [26, 26],
    [50, 50],
    [74, 74],
  ],
}

interface DieProps {
  die: DieId
  value?: 1 | 2 | 3
  crazyColor?: 'black' | 'white'
  size?: number
}

export function Die({ die, value, crazyColor, size = 38 }: DieProps) {
  const f = DIE_FILL[die]
  const pips = value ? PIPS[value] : []
  const faceDown = value === undefined
  return (
    <div
      style={{
        position: 'relative',
        width: size,
        height: size,
        borderRadius: size * 0.24,
        background: `linear-gradient(155deg, ${f.light} 0%, ${f.body} 55%, ${f.dark} 100%)`,
        boxShadow:
          'inset 0 2px 0 rgba(255,255,255,0.4), inset 0 -3px 5px rgba(0,0,0,0.28), 0 2px 5px rgba(60,36,10,0.35)',
        flex: '0 0 auto',
        opacity: faceDown ? 0.92 : 1,
      }}
    >
      {/* Crazy-camel color indicator */}
      {die === 'grey' && crazyColor && (
        <span
          style={{
            position: 'absolute',
            top: size * 0.1,
            left: size * 0.1,
            width: size * 0.22,
            height: size * 0.22,
            borderRadius: '50%',
            background: crazyColor === 'white' ? '#fff' : '#111',
            border: '1.5px solid rgba(255,255,255,0.7)',
          }}
        />
      )}
      {faceDown ? (
        // Still in the pyramid — show a faint pyramid mark.
        <svg
          viewBox="0 0 24 24"
          width={size * 0.5}
          height={size * 0.5}
          style={{ position: 'absolute', inset: 0, margin: 'auto' }}
        >
          <path d="M12 4 L21 20 H3 Z" fill="rgba(255,255,255,0.55)" />
        </svg>
      ) : (
        pips.map(([x, y], i) => (
          <span
            key={i}
            style={{
              position: 'absolute',
              left: `${x}%`,
              top: `${y}%`,
              width: size * 0.16,
              height: size * 0.16,
              marginLeft: -size * 0.08,
              marginTop: -size * 0.08,
              borderRadius: '50%',
              background: f.ink,
              boxShadow: 'inset 0 -1px 1px rgba(0,0,0,0.35)',
            }}
          />
        ))
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Coin
// ---------------------------------------------------------------------------

export function CoinIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{ display: 'block', flex: '0 0 auto' }}>
      <defs>
        <radialGradient id="cu-coin" cx="38%" cy="32%" r="75%">
          <stop offset="0%" stopColor="#FCE08A" />
          <stop offset="55%" stopColor={CU.coin} />
          <stop offset="100%" stopColor={CU.coinDeep} />
        </radialGradient>
      </defs>
      <circle cx="16" cy="16" r="15" fill="url(#cu-coin)" stroke={CU.coinDeep} strokeWidth="1.5" />
      <circle cx="16" cy="16" r="11" fill="none" stroke="rgba(169,120,28,0.6)" strokeWidth="1" />
      {/* desert sun */}
      <g fill="#fff6df">
        <circle cx="16" cy="16" r="3.4" />
        {Array.from({ length: 8 }).map((_, i) => {
          const a = (i * Math.PI) / 4
          const x1 = 16 + Math.cos(a) * 5.5
          const y1 = 16 + Math.sin(a) * 5.5
          const x2 = 16 + Math.cos(a) * 8
          const y2 = 16 + Math.sin(a) * 8
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#fff6df" strokeWidth="1.4" strokeLinecap="round" />
        })}
      </g>
    </svg>
  )
}

interface CoinsProps {
  amount: number
  size?: number
  big?: boolean
}

/** Coin icon + amount, the amount set in the chunky display face. */
export function Coins({ amount, size = 18, big = false }: CoinsProps) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <CoinIcon size={big ? size * 1.4 : size} />
      <span
        style={{
          fontFamily: CU_DISPLAY,
          fontSize: big ? size * 1.5 : size,
          lineHeight: 1,
          color: CU.coinDeep,
          letterSpacing: '0.02em',
        }}
      >
        {amount}
      </span>
    </span>
  )
}

// ---------------------------------------------------------------------------
// Betting slip (leg-bet ticket)
// ---------------------------------------------------------------------------

interface SlipProps {
  color: RacingColor
  value: number
  /** Smaller variant for dense lists. */
  compact?: boolean
  /** Dim to indicate it's a board-available ticket, not held. */
  dim?: boolean
  onClick?: () => void
  disabled?: boolean
}

/**
 * A perforated betting slip. Camel-color header band with the camel, a big
 * value numeral, and a dashed "tear" line — reads as a physical ticket.
 */
export function BettingSlip({ color, value, compact = false, dim = false, onClick, disabled }: SlipProps) {
  const c = CAMEL_HEX[color]
  const w = compact ? 52 : 66
  const interactive = Boolean(onClick) && !disabled
  return (
    <div
      onClick={interactive ? onClick : undefined}
      style={{
        width: w,
        borderRadius: 8,
        overflow: 'hidden',
        background: CU.surfaceSolid,
        border: `1px solid ${c.dark}`,
        boxShadow: dim ? 'none' : '0 2px 6px rgba(60,36,10,0.25)',
        opacity: disabled ? 0.4 : dim ? 0.85 : 1,
        cursor: interactive ? 'pointer' : 'default',
        flex: '0 0 auto',
        transition: 'transform 120ms ease, box-shadow 120ms ease',
      }}
    >
      <div
        style={{
          background: `linear-gradient(135deg, ${c.light}, ${c.body} 60%, ${c.dark})`,
          padding: compact ? '3px 0' : '5px 0',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <CamelGlyph color={color} size={compact ? 16 : 22} noShadow />
      </div>
      {/* perforation */}
      <div style={{ borderTop: `2px dashed ${c.dark}`, opacity: 0.35 }} />
      <div
        style={{
          fontFamily: CU_DISPLAY,
          fontSize: compact ? 20 : 28,
          textAlign: 'center',
          color: c.dark,
          padding: compact ? '1px 0 3px' : '2px 0 5px',
          lineHeight: 1,
        }}
      >
        {value}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Race-bet card
// ---------------------------------------------------------------------------

interface RaceCardProps {
  /** Face-up camel color, or undefined to render the face-down card back. */
  color?: RacingColor
  pile?: 'winner' | 'loser'
  size?: 'sm' | 'md'
  onClick?: () => void
  disabled?: boolean
  selected?: boolean
}

export function RaceCard({ color, pile, size = 'md', onClick, disabled, selected }: RaceCardProps) {
  const dims = size === 'sm' ? { w: 34, h: 46 } : { w: 48, h: 66 }
  const interactive = Boolean(onClick) && !disabled
  if (color === undefined) {
    // Face-down back — papyrus with a brass diamond motif.
    return (
      <div
        style={{
          width: dims.w,
          height: dims.h,
          borderRadius: 7,
          background: `repeating-linear-gradient(45deg, ${CU.coinDeep}22, ${CU.coinDeep}22 4px, transparent 4px, transparent 8px), linear-gradient(160deg, #f3e2bd, #e6cf9e)`,
          border: `1.5px solid ${CU.coinDeep}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: '0 0 auto',
          boxShadow: '0 1px 4px rgba(60,36,10,0.3)',
        }}
      >
        <div
          style={{
            width: dims.w * 0.42,
            height: dims.w * 0.42,
            transform: 'rotate(45deg)',
            border: `2px solid ${CU.coinDeep}`,
            opacity: 0.7,
          }}
        />
      </div>
    )
  }
  const c = CAMEL_HEX[color]
  return (
    <div
      onClick={interactive ? onClick : undefined}
      style={{
        width: dims.w,
        height: dims.h,
        borderRadius: 7,
        background: `linear-gradient(160deg, ${c.light}, ${c.body} 60%, ${c.dark})`,
        border: selected ? `2.5px solid ${CU.brass}` : `1.5px solid ${c.dark}`,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        flex: '0 0 auto',
        cursor: interactive ? 'pointer' : 'default',
        boxShadow: selected ? `0 0 0 3px ${CU.brass}44, 0 3px 8px rgba(60,36,10,0.4)` : '0 2px 6px rgba(60,36,10,0.35)',
        opacity: disabled ? 0.45 : 1,
        transition: 'transform 120ms ease',
      }}
    >
      <CamelGlyph color={color} size={size === 'sm' ? 18 : 26} noShadow />
      {pile && (
        <span
          style={{
            position: 'absolute',
            top: 2,
            right: 4,
            fontFamily: CU_HEADING,
            fontWeight: 900,
            fontSize: size === 'sm' ? 11 : 14,
            color: c.ink,
            textShadow: '0 1px 1px rgba(0,0,0,0.3)',
          }}
        >
          {pile === 'winner' ? 'W' : 'L'}
        </span>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Wordmark + panel chrome
// ---------------------------------------------------------------------------

interface WordmarkProps {
  size?: number
  /** Subtitle to the right (e.g. "Room ABCD · Leg 2"). */
  trailing?: ReactNode
}

export function Wordmark({ size = 34, trailing }: WordmarkProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <CamelGlyph color="yellow" size={size * 1.1} />
        <span
          style={{
            fontFamily: CU_DISPLAY,
            fontSize: size,
            lineHeight: 0.9,
            color: CU.ink,
            letterSpacing: '0.01em',
            textShadow: '0 2px 0 rgba(255,255,255,0.25)',
          }}
        >
          Camel&nbsp;Up
        </span>
      </div>
      {trailing}
    </div>
  )
}

/** Small uppercase brass section label in the heading face. */
export function PanelTitle({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div
      style={{
        fontFamily: CU_HEADING,
        fontWeight: 600,
        fontSize: 15,
        textTransform: 'uppercase',
        letterSpacing: '0.12em',
        color: CU.brass,
        marginBottom: 10,
        ...style,
      }}
    >
      {children}
    </div>
  )
}

/** Reusable papyrus panel wrapper. */
export function Panel({
  children,
  style,
  pad = 16,
}: {
  children: ReactNode
  style?: CSSProperties
  pad?: number
}) {
  return (
    <div
      style={{
        background: CU.panelBg,
        border: `1px solid ${CU.border}`,
        borderRadius: 14,
        padding: pad,
        boxShadow: CU.shadowSoft,
        ...style,
      }}
    >
      {children}
    </div>
  )
}

/** Color swatch dot for a racing camel (used in legends). */
export function CamelDot({ color, size = 12 }: { color: CamelColor; size?: number }) {
  const c = CAMEL_HEX[color]
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: `linear-gradient(135deg, ${c.light}, ${c.dark})`,
        border: `1px solid ${c.dark}`,
        display: 'inline-block',
        flex: '0 0 auto',
      }}
    />
  )
}
